import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './core/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import InstallPWA from './components/InstallPWA';
import Monitoring from './pages/Monitoring';
import Security from './pages/Security';
import Outreach from './pages/Outreach';
import Payments from './pages/Payments';
import MetaAds from './pages/MetaAds';

// Lazy-loaded pages — split into separate chunks at build time
const Dashboard      = lazy(() => import('./pages/Dashboard'));
const CRM            = lazy(() => import('./pages/CRM'));
const Settings       = lazy(() => import('./pages/Settings'));
const CalendarPage   = lazy(() => import('./pages/Calendar').then(m => ({ default: m.CalendarPage })));
const ResetPassword  = lazy(() => import('./pages/ResetPassword'));
const Inbox          = lazy(() => import('./pages/Inbox'));
const Marketing      = lazy(() => import('./pages/Marketing'));
const Treatments     = lazy(() => import('./pages/Treatments'));
const Cobros         = lazy(() => import('./pages/Cobros'));
const ListaEspera    = lazy(() => import('./pages/ListaEspera'));
const Fugas          = lazy(() => import('./pages/Fugas'));
const Register       = lazy(() => import('./pages/Register'));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));
const MarketingLeads  = lazy(() => import('./pages/MarketingLeads'));
const AdminOrgs       = lazy(() => import('./pages/AdminOrgs'));
const PrivacyPolicy   = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService  = lazy(() => import('./pages/TermsOfService'));
const RefundPolicy    = lazy(() => import('./pages/RefundPolicy'));
const LandingV2          = lazy(() => import('./pages/LandingV2'));
const LandingV3          = lazy(() => import('./pages/LandingV3'));
const OnboardingAudit    = lazy(() => import('./pages/OnboardingAudit'));
const FiltroUnaCosa      = lazy(() => import('./pages/FiltroUnaCosa'));
const ErroresWhatsappClinicas = lazy(() => import('./pages/ErroresWhatsappClinicas'));
const BlogIndex          = lazy(() => import('./pages/blog/BlogIndex'));
const BlogPost           = lazy(() => import('./pages/blog/BlogPost'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-full min-h-[200px]">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function AppRoutes() {
  const { isAuthenticated, isSuperAdmin, login } = useAuth();

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/" /> : <Login onLogin={login} />
        } />
        <Route path="/reset-password" element={<ResetPassword />} />

        {isAuthenticated ? (
          <Route element={<Layout />}>
            <Route path="/" element={
              isSuperAdmin ? <Navigate to="/admin/marketing-leads" replace /> : <Dashboard />
            } />
            <Route path="/inbox" element={<Inbox />} />
            <Route path="/crm" element={<CRM />} />
            <Route path="/marketing" element={<Marketing />} />
            <Route path="/treatments" element={<Treatments />} />
            <Route path="/cobros" element={<Cobros />} />
            <Route path="/lista-espera" element={<ListaEspera />} />
            <Route path="/fugas" element={<Fugas />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/monitoring" element={
              isSuperAdmin ? <Monitoring /> : <Navigate to="/" replace />
            } />
            <Route path="/security" element={
              isSuperAdmin ? <Security /> : <Navigate to="/" replace />
            } />
            <Route path="/meta-ads" element={
              isSuperAdmin ? <MetaAds /> : <Navigate to="/" replace />
            } />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/admin/marketing-leads" element={
              isSuperAdmin ? <MarketingLeads /> : <Navigate to="/" replace />
            } />
            <Route path="/admin/outreach" element={
              isSuperAdmin ? <Outreach /> : <Navigate to="/" replace />
            } />
            <Route path="/admin/pagos" element={
              isSuperAdmin ? <Payments /> : <Navigate to="/" replace />
            } />
            <Route path="/admin/orgs" element={
              isSuperAdmin ? <AdminOrgs /> : <Navigate to="/" replace />
            } />
          </Route>
        ) : null}

        <Route path="/register" element={<Register />} />
        <Route path="/onboarding" element={<OnboardingAudit />} />
        <Route path="/checkout-success" element={<CheckoutSuccess />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/refund" element={<RefundPolicy />} />
        <Route path="/recursos/filtro-1-sola-cosa" element={<FiltroUnaCosa />} />
        <Route path="/recursos/errores-whatsapp-clinicas" element={<ErroresWhatsappClinicas />} />
        <Route path="/recursos/5-errores-whatsapp-dental" element={<ErroresWhatsappClinicas />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/lp3" element={<LandingV3 />} />
        {!isAuthenticated && <Route path="/" element={<LandingV2 />} />}

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
        <InstallPWA />
      </Router>
    </AuthProvider>
  );
}

export default App;
